import type { ToolLifecycle } from '../tools/tool-lifecycle'
/**
 * openai-responses-stream.ts
 * OpenAI Responses API（/v1/responses）原生流式解析。
 *
 * OpenAI 兼容模式（/v1/chat/completions）拿不到 GPT 的思考摘要；
 * 必须走 Responses API 并配 reasoning:{summary:'auto'}，才会吐
 * response.reasoning_summary_text.delta（可读思考摘要）。
 * 实测：正文走 response.output_text.delta。
 */
import type { StreamEvent } from './llm'

import { duplicateToolResult, getToolLifecycleByName, ignoredAfterTerminalResult, normalizeToolResult, ToolExecutionLedger, ToolInputError } from '../tools/tool-lifecycle'
import { moveNonStableSystemLayersToLatestUser } from '../utils/prompt-cache-layout'
import { logCacheUsage } from './llm-cache-diagnostics'
import { parseToolArguments } from './openai-stream-tools'
import { getDisallowedToolCallResult, shouldUsePlainTextFallback } from './structured-output'

/** 把单个 Responses SSE 事件映射为统一 StreamEvent；无关事件返回 null。 */
export function parseResponsesEvent(
  evt: { type?: string, delta?: string },
): StreamEvent | null {
  switch (evt.type) {
    case 'response.reasoning_summary_text.delta':
    case 'response.reasoning_text.delta':
      return { type: 'reasoning-delta', text: evt.delta ?? '' }
    case 'response.output_text.delta':
      return { type: 'text-delta', text: evt.delta ?? '' }
    default:
      return null
  }
}

/** Responses input 消息项（role + content）。 */
interface ResponsesMsg { role: string, content: unknown, metadata?: unknown }

/**
 * OpenAI 风格 messages → Responses input。
 * system 消息滤出，字符串 content 用 \n 拼成 instructions；其余原样进 input。
 */
export function messagesToResponsesInput(
  messages: ResponsesMsg[],
): { instructions: string, input: ResponsesMsg[] } {
  const normalizedMessages = moveNonStableSystemLayersToLatestUser(messages)
  const sys = normalizedMessages
    .filter(m => m.role === 'system')
    .map(m => (typeof m.content === 'string' ? m.content : ''))
    .join('\n')
  const input = normalizedMessages.filter(m => m.role !== 'system')
  return { instructions: sys, input }
}

/** OpenAI 风格 function tool。 */
interface ChatFnTool { type: 'function', function: { name: string, description?: string, parameters?: unknown } }

/** OpenAI chat function tool → Responses 扁平 function 格式。 */
export function toolsToResponsesTools(tools: ChatFnTool[]) {
  return tools.map(t => ({
    type: 'function' as const,
    name: t.function.name,
    description: t.function.description,
    parameters: t.function.parameters,
  }))
}

/** 单次 Responses 流的聚合结果（供工具循环消费）。 */
export interface ResponsesToolCall {
  callId: string
  name: string
  args: unknown
  /** 参数 JSON 无法解析时，阻止工具执行并把错误回传给模型。 */
  argsError?: string
  /** 保留模型原始参数，下一轮回送时不能改写成 {}。 */
  rawArguments?: string
}

export interface ResponsesStreamResult {
  textContent: string
  reasoningContent: string
  /** Responses function_call：有 call_id + name + arguments(JSON 字符串)。 */
  toolCalls: ResponsesToolCall[]
}

/**
 * 单次 POST /v1/responses，解析 SSE：
 * - response.reasoning_summary_text.delta / reasoning_text.delta → reasoning-delta
 * - response.output_text.delta                                   → text-delta
 * - response.output_item.done(type=function_call)               → 收集工具调用
 * 与 executeOpenAICompatStream / executeGeminiStream 对称：手搓请求、复用注入的 fetch、可离线单测。
 */
export async function executeResponsesStream(
  url: string,
  body: Record<string, unknown>,
  headers: Record<string, string>,
  onEvent: (e: StreamEvent) => void | Promise<void>,
  fetchImpl: typeof fetch,
  abortSignal?: AbortSignal,
): Promise<ResponsesStreamResult> {
  const model = String(body.model ?? 'unknown')
  const serializedBody = JSON.stringify(body)
  let res: Response
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers,
      body: serializedBody,
      ...(abortSignal ? { signal: abortSignal } : {}),
    })
  }
  catch (error) {
    logCacheUsage('OpenAI', model)
    throw error
  }
  if (!res.ok || !res.body) {
    logCacheUsage('OpenAI', model)
    const err = new Error(`OpenAI Responses HTTP ${res.status}`) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let textContent = ''
  let reasoningContent = ''
  const toolCalls: ResponsesToolCall[] = []
  let lastUsage: { promptTokens?: number, cachedTokens?: number } | null = null

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done)
        break
      const decoded = decoder.decode(value, { stream: true })
      buf += decoded
      const lines = buf.split('\n')
      buf = lines.pop() ?? ''
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith('data:'))
          continue
        const json = trimmed.slice(5).trim()
        if (!json || json === '[DONE]')
          continue
        let parsed: {
          type?: string
          delta?: string
          item?: { type?: string, call_id?: string, name?: string, arguments?: string }
          response?: { usage?: { input_tokens?: number, input_tokens_details?: { cached_tokens?: number } } }
        }
        try {
          parsed = JSON.parse(json)
        }
        catch {
          continue
        }
        // response.completed 事件含完整 usage
        if (parsed.type === 'response.completed' && parsed.response?.usage) {
          const u = parsed.response.usage
          lastUsage = {
            promptTokens: u.input_tokens,
            cachedTokens: Object.prototype.hasOwnProperty.call(u.input_tokens_details ?? {}, 'cached_tokens') ? u.input_tokens_details?.cached_tokens : undefined,
          }
        }
        // function_call 完成事件：收集工具调用
        if (parsed.type === 'response.output_item.done' && parsed.item?.type === 'function_call') {
          const it = parsed.item
          const name = it.name ?? ''
          if (!name)
            continue
          const rawArguments = it.arguments ?? ''
          const { input: args, error: argsError } = parseToolArguments(rawArguments)
          const callId = it.call_id ?? name
          toolCalls.push({ callId, name, args, argsError, ...(argsError ? { rawArguments } : {}) })
          await onEvent({ type: 'tool-call-streaming-start', toolCallId: callId, toolName: name })
          await onEvent({ type: 'tool-call', toolCallId: callId, toolName: name, args } as StreamEvent)
          continue
        }
        // 文本 / 思考增量
        const mapped = parseResponsesEvent(parsed)
        if (!mapped)
          continue
        if (mapped.type === 'reasoning-delta')
          reasoningContent += mapped.text
        else if (mapped.type === 'text-delta')
          textContent += mapped.text
        await onEvent(mapped)
      }
    }
  }
  finally {
    reader.cancel().catch(() => {})
  }
  logCacheUsage('OpenAI', model, lastUsage
    ? {
        totalInputTokens: lastUsage.promptTokens,
        cachedInputTokens: lastUsage.cachedTokens,
      }
    : undefined)

  return { textContent, reasoningContent, toolCalls }
}

/** runResponsesToolLoop 的依赖（与 CompatLoopDeps / GeminiLoopDeps 对称）。 */
export interface ResponsesLoopDeps {
  streamOnce: (input: unknown[]) => Promise<ResponsesStreamResult>
  toolExecutors: Map<string, (input: unknown, opts: unknown) => Promise<unknown>>
  terminalToolNames: Set<string>
  maxSteps: number
  onEvent: (e: StreamEvent) => void | Promise<void>
  ledger?: ToolExecutionLedger
  onToolBudgetExceeded?: () => void
  actionToolNames?: ReadonlySet<string>
  lifecycleByName?: ReadonlyMap<string, ToolLifecycle>
  requireToolCall?: boolean
  allowedToolNames?: ReadonlySet<string>
}

/**
 * Responses 多步工具循环，复刻终止语义，但用 Responses input 格式：
 * - 上一轮的 function_call 项 + 结果项 { type:'function_call_output', call_id, output } 追加进下一轮 input。
 * 终止工具执行后不回送 function_call_output 且结束本轮。
 */
export async function runResponsesToolLoop(
  initialInput: unknown[],
  deps: ResponsesLoopDeps,
): Promise<void> {
  const { streamOnce, toolExecutors, terminalToolNames, maxSteps, onEvent } = deps
  let input = [...initialInput]
  const ledger = deps.ledger ?? new ToolExecutionLedger(maxSteps)

  for (let step = 0; step < maxSteps; step++) {
    const result = await streamOnce(input)
    if (deps.requireToolCall && result.textContent.trim())
      await onEvent({ type: 'text-delta', text: result.textContent })

    if (result.toolCalls.length === 0) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    if (shouldUsePlainTextFallback(
      result.toolCalls.map(tool => tool.name),
      result.textContent,
      deps.requireToolCall,
      deps.allowedToolNames,
    )) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    // 上一轮的 function_call 项（Responses 要求回送时带上原 call 项）
    const callItems = result.toolCalls.map(tc => ({
      type: 'function_call',
      call_id: tc.callId,
      name: tc.name,
      arguments: tc.rawArguments ?? JSON.stringify(tc.args ?? {}),
    }))

    const outputItems: Array<Record<string, unknown>> = []
    let hitTerminal = false
    let hitBudget = false
    for (const tc of result.toolCalls) {
      if (hitTerminal) {
        await onEvent({ type: 'tool-result', toolCallId: tc.callId, result: ignoredAfterTerminalResult() })
        continue
      }
      if (deps.allowedToolNames && !deps.allowedToolNames.has(tc.name)) {
        outputItems.push({
          type: 'function_call_output',
          call_id: tc.callId,
          output: getDisallowedToolCallResult(tc.name, deps.allowedToolNames),
        })
        continue
      }
      const lifecycle = deps.actionToolNames?.has(tc.name) ? 'action' : getToolLifecycleByName(tc.name, deps.lifecycleByName, terminalToolNames)
      const claim = ledger.claim(tc.callId, tc.name, tc.args)
      if (!claim.accepted) {
        const content = duplicateToolResult(claim)
        await onEvent({ type: 'tool-result', toolCallId: tc.callId, result: content })
        if (claim.reason === 'budget') {
          ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
          hitBudget = true
          break
        }
        outputItems.push({ type: 'function_call_output', call_id: tc.callId, output: content })
        continue
      }
      const executor = toolExecutors.get(tc.name)
      let content = ''
      let executionError: unknown
      if (tc.argsError) {
        executionError = new ToolInputError(tc.argsError)
        content = tc.argsError
      }
      else if (executor) {
        try {
          const r = await executor(tc.args, {})
          content = normalizeToolResult(r, tc.name, lifecycle)
        }
        catch (e) {
          executionError = e
          content = e instanceof Error ? e.message : String(e)
        }
      }
      else {
        if (lifecycle === 'terminal')
          throw new Error(`Terminal tool "${tc.name}" is not registered`)
        content = `Tool "${tc.name}" not found`
      }
      await onEvent({ type: 'tool-result', toolCallId: tc.callId, result: content })

      if (lifecycle === 'terminal') {
        if (executionError && !ledger.allowInputCorrection(executionError, tc.callId, tc.name, tc.args)) {
          if (executionError instanceof ToolInputError) {
            await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
            return
          }
          throw executionError
        }
        if (!executionError) {
          hitTerminal = true
          continue
        }
      }
      if (ledger.budgetReached) {
        ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
        hitBudget = true
        break
      }
      outputItems.push({ type: 'function_call_output', call_id: tc.callId, output: content })
    }

    if (hitTerminal || hitBudget || outputItems.length === 0) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    input = [...input, ...callItems, ...outputItems]
  }

  await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
}
